import { createParagraph } from '@lumapps/wrex-typography/utils/createParagraph';
import { Editor, Path, Range, ReactEditor, Transforms } from '@lumapps/wrex/slate';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';

import { T_D, T_H, T_R } from '../constants';
import type { TableEditor, TDElement, THElement, TRElement } from '../types';
import { getCurrentCellsInRow } from './getCurrentCellsInRow';
import { getCurrentSelectionInfos } from './getCurrentSelectionInfos';
import { isHeaderRow } from './isHeaderRow';
import { setBoldOnCells } from './setBoldOnCells';

interface AddRowOptions {
    at?: Path;
    preventFocus?: boolean;
}

/** Add a row before or after the current position in a table. */
export const addRow = (editor: ReactEditor & TableEditor, position: 'before' | 'after', options?: AddRowOptions) => {
    /* get selection details (null if no selection or if selection not in table) */
    const selectionInfos = getCurrentSelectionInfos(editor);

    if (!selectionInfos) {
        return undefined;
    }

    const { isSelectionInOneCell, startRow, endRow } = selectionInfos;

    const tablePath = [(options?.at || Range.start(editor.selection as Range).path)[0]];

    /* inserting a row before the header row (row 0) would push the header row down without
     * demoting it, leaving the table with a header row that's no longer first. Keep the header
     * anchored to row 0 by making the new row the header instead, and demoting the old one. */
    const insertsAboveHeaderRow = position === 'before' && startRow === 0 && isHeaderRow(editor, tablePath);

    /* get the cells to add in the new row */
    const getNewCells = () => {
        /* get the number of cell to add in the row */
        const { cellsInRow } = getCurrentCellsInRow(editor, [...tablePath, startRow]);
        if (insertsAboveHeaderRow) {
            return cellsInRow.map(
                ([cell]) =>
                    ({
                        type: T_H,
                        headerType: (cell as THElement).headerType || 'col',
                        children: [createParagraph()],
                    }) as THElement,
            );
        }
        const cellsToAdd = cellsInRow.map(() => ({ type: T_D, children: [createParagraph()] }) as TDElement);
        return cellsToAdd;
    };

    /* build the new row */
    const node: TRElement = {
        type: T_R,
        children: getNewCells(),
    };

    /* define the position to insert row (before or after the current position in table) */

    const getInsertionRow = () => {
        if (position === 'before') {
            return startRow;
        }
        return isSelectionInOneCell ? startRow + 1 : endRow + 1;
    };

    /* insert the new row */
    Transforms.insertNodes(editor, node, { at: [...tablePath, getInsertionRow()] });

    if (insertsAboveHeaderRow) {
        const { cellsInRow: formerHeaderCells } = getCurrentCellsInRow(editor, [...tablePath, 1]);
        const { cellsInRow: newHeaderCells } = getCurrentCellsInRow(editor, [...tablePath, 0]);
        Editor.withoutNormalizing(editor, () => {
            formerHeaderCells.forEach(([, cellPath]) => {
                Transforms.setNodes<TDElement>(editor, { type: T_D }, { at: cellPath });
                Transforms.unsetNodes(editor, 'headerType', { at: cellPath });
            });
            /* the header emphasis moves along with the header row */
            setBoldOnCells(
                editor,
                formerHeaderCells.map(([, cellPath]) => cellPath),
                false,
            );
            setBoldOnCells(
                editor,
                newHeaderCells.map(([, cellPath]) => cellPath),
                true,
            );
        });
    }

    /* focus on the first cell of the new row */
    const newCellPosition = {
        anchor: {
            path: [...tablePath, getInsertionRow(), 0, 0, 0],
            offset: 0,
        },
        focus: {
            path: [...tablePath, getInsertionRow(), 0, 0, 0],
            offset: 0,
        },
    };

    // For some reason, the focusAt doesn't work properly in the table context if we do not delay it.
    if (!options?.preventFocus) {
        setTimeout(() => {
            focusAt(editor, newCellPosition);
        }, 1);
    }
    return newCellPosition;
};
